/**
 * Promoted Intents helpers.
 */

import type { KnowledgeBlueprint, KnowledgeIntentGroup } from './types';

/** snake_case / kebab intent key -> Title Case label, e.g. "donation_request" -> "Donation Request". */
export function formatIntentLabel(intent: string): string {
  if (!intent) return '';
  return intent
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Clean a raw customer query for display: drop the "[subject: …] ==>" prefix and
 * "[Customer uploaded …]" attachment noise, collapse whitespace. */
export function formatQuery(question?: string): string {
  if (!question) return '';
  return question
    .replace(/^\s*\[subject:[^\]]*\]\s*==>\s*/i, '')
    .replace(/\[Customer uploaded[^\]]*\]/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** A sub-intent is enabled (auto-send) unless its index is explicitly set to false. */
export function isTemplateEnabled(templatesEnabled: Record<string, boolean>, index: number): boolean {
  const value = templatesEnabled[String(index)];
  return value === undefined ? true : value;
}

/** Display name for a sub-intent at a given index, with a stable fallback. */
export function getSubIntentName(templateNames: Record<string, string>, index: number): string {
  const name = templateNames[String(index)];
  return name && name.trim() ? name.trim() : `Sub-intent ${index + 1}`;
}

/** "How/when this sub-intent is reached" detail (display-only); empty string if none. */
export function getSubIntentDetail(templateDetails: Record<string, string>, index: number): string {
  const detail = templateDetails?.[String(index)];
  return detail && detail.trim() ? detail.trim() : '';
}

/**
 * Group a flat list of knowledge queries by their `intent` field, counting how many are promoted
 * and how many of those are switched on (serving). A candidate is never counted as serving.
 * Groups preserve first-seen order; queries keep their original order.
 */
export function groupKnowledgeByIntent(queries: KnowledgeBlueprint[]): KnowledgeIntentGroup[] {
  const byIntent = new Map<string, KnowledgeBlueprint[]>();
  for (const query of queries) {
    const existing = byIntent.get(query.intent);
    if (existing) {
      existing.push(query);
    } else {
      byIntent.set(query.intent, [query]);
    }
  }

  return Array.from(byIntent.entries()).map(([intent, intentQueries]) => {
    const promoted = intentQueries.filter((query) => query.state === 'promoted');
    return {
      intent,
      queries: intentQueries,
      total: intentQueries.length,
      promoted: promoted.length,
      serving: promoted.filter((query) => query.enabled).length
    };
  });
}

/** What an intent serves, in words: "Not promoted", "Paused: 0 of 2 serving", "Serving: 2 of 5 promoted". */
export function intentServingStatus(group: Pick<KnowledgeIntentGroup, 'total' | 'promoted' | 'serving'>): string {
  if (group.promoted === 0) return 'Not promoted';
  if (group.serving === 0) return `Paused: 0 of ${group.promoted} serving`;
  return `Serving: ${group.serving} of ${group.total} promoted`;
}
